import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppText, { FormHeader } from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Contract from "../../models/Contract";
import Customer from "../../models/Customer";
import Organisation from "../../models/Organisation";
import { useContract } from "../../networking/contract.hooks";
import { useUpdateCustomer } from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { formatCurrencyString } from "../../utils/string.utils";

const CustomerContractTab: React.FC<{ customer: Customer; contracts: Contract[] }> = ({
	customer,
	contracts,
}) => {
	const { mutate: updateCustomer } = useUpdateCustomer();
	const { data: organisation } = useOrganisation();

	const { t } = useTranslation();

	const [selectedContract, setSelectedContract] = useState(
		contracts.find((c) => c.id === customer.contract.id)
	);

	const contractId = selectedContract?.id || customer.contract.id;
	const { contract, isInitialLoading: loading } = useContract(
		{ contractId, inheritFromFallback: true },
		!!contractId
	);

	const handleSubmit = () => {
		if (customer?.id && selectedContract) {
			// The hook toasts success/error and refetches the customer.
			updateCustomer({
				customerId: customer.id,
				update: { contract: { id: selectedContract.id } },
			});
		}
	};

	const sortedPrices = (contract?.prices || [])
		.map((price) => ({
			name: getServiceName(organisation, price.branch, price.service).join(", "),
			price: price.price,
		}))
		.sort((a, b) => a.name.localeCompare(b.name));

	return (
		<div>
			<Flex maxWidth="1000px" direction="column" margin="2rem 0 4rem 0">
				<Dropdown<Contract, Contract>
					label="CONTRACT"
					placeholder="SELECT_CONTRACT"
					name="contract"
					getOptionLabel={(option) => option.name}
					options={contracts}
					value={selectedContract}
					getValue={(c) => c}
					onChange={setSelectedContract}
				/>

				<FormHeader>
					{t("CONTRACT_PRICE_LIST")} ({selectedContract?.name})
				</FormHeader>
				<AppText margin="0 0 1.5rem 0">{t("CONTRACT_PRICE_LIST_INFO")}</AppText>
				{loading ? (
					<LoadingIndicator />
				) : (
					<TableLayout selectable={false}>
						<table>
							<thead></thead>
							<tbody>
								{sortedPrices.map((price, i) => (
									<tr key={contract!.id + i}>
										<td>{price.name}</td>
										<td>
											{formatCurrencyString(price.price || 0)} / {t("H")}{" "}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</TableLayout>
				)}
			</Flex>
			<FloatingButtonPanel>
				<Button width="9.75rem" onClick={handleSubmit}>
					{t("SAVE_CHANGES")}
				</Button>
			</FloatingButtonPanel>
		</div>
	);
};

export default CustomerContractTab;

function getServiceName(organisation: Organisation | undefined, branch: string, service: string) {
	let foundBranch;
	let foundService;
	if (organisation?.competences)
		foundBranch = organisation.competences.find((competence) => {
			return competence.key === branch;
		});

	foundService = (foundBranch?.items || []).find((item) => {
		return item.key === service;
	});

	return [foundBranch?.name.sv, foundService?.name.sv];
}
